import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, ChevronDown, Filter } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import Button from '../components/Button'
import api from '../services/api'
import { trackEvent } from '../utils/mixpanel'
import ExplorerEventCard from '../components/events/explorer/ExplorerEventCard'
import EventFilterSidebar from '../components/events/explorer/EventFilterSidebar'
import EventFilterSheet from '../components/events/explorer/EventFilterSheet'
import {
  DEFAULT_FILTERS,
  TYPE_OPTIONS,
  applyEventFilters,
  computeLocationCounts,
  computeTypeCounts,
  filtersToSearchParams,
  searchParamsToFilters,
} from '../utils/eventFilters'

const DATE_LABELS = {
  today: 'today',
  tomorrow: 'tomorrow',
  week: 'this week',
  'next-week': 'next week',
  'two-weeks': 'next 2 weeks',
}

const shortDate = (d) => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })

// "Bangalore · this week · yoga & breathwork · free · “breath”"
function contextLine(filters) {
  const parts = []
  if (filters.location === 'online') parts.push('Online')
  else if (filters.location !== 'all') parts.push(filters.location)
  if (filters.date.kind === 'custom' && filters.date.start) {
    parts.push(`${shortDate(filters.date.start)} – ${shortDate(filters.date.end || filters.date.start)}`)
  } else if (DATE_LABELS[filters.date.kind]) {
    parts.push(DATE_LABELS[filters.date.kind])
  }
  if (filters.types.length > 0) {
    const labels = TYPE_OPTIONS.filter((t) => filters.types.includes(t.value)).map((t) =>
      t.label.toLowerCase()
    )
    parts.push(labels.join(' & '))
  }
  if (filters.cost !== 'all') parts.push(filters.cost)
  if (filters.query.trim()) parts.push(`“${filters.query.trim()}”`)
  return parts.length > 0 ? parts.join(' · ') : 'All upcoming experiences'
}

// Mobile quick-filter chips — each toggles one facet of the shared
// filters object (same toggle semantics as the sidebar shortcuts).
const QUICK_CHIPS = [
  {
    key: 'week',
    label: 'This week',
    isActive: (f) => f.date.kind === 'week',
    toggle: (f) => ({ ...f, date: f.date.kind === 'week' ? { kind: 'all' } : { kind: 'week' } }),
  },
  {
    key: 'today',
    label: 'Today',
    isActive: (f) => f.date.kind === 'today',
    toggle: (f) => ({ ...f, date: f.date.kind === 'today' ? { kind: 'all' } : { kind: 'today' } }),
  },
  {
    key: 'next-week',
    label: 'Next week',
    isActive: (f) => f.date.kind === 'next-week',
    toggle: (f) => ({
      ...f,
      date: f.date.kind === 'next-week' ? { kind: 'all' } : { kind: 'next-week' },
    }),
  },
  {
    key: 'two-weeks',
    label: 'Next 2 weeks',
    isActive: (f) => f.date.kind === 'two-weeks',
    toggle: (f) => ({
      ...f,
      date: f.date.kind === 'two-weeks' ? { kind: 'all' } : { kind: 'two-weeks' },
    }),
  },
  {
    key: 'free',
    label: 'Free',
    isActive: (f) => f.cost === 'free',
    toggle: (f) => ({ ...f, cost: f.cost === 'free' ? 'all' : 'free' }),
  },
  {
    key: 'online',
    label: 'Online',
    isActive: (f) => f.location === 'online',
    toggle: (f) => ({ ...f, location: f.location === 'online' ? 'all' : 'online' }),
  },
  ...TYPE_OPTIONS.slice(0, 2).map(({ value, label }) => ({
    key: value,
    label,
    isActive: (f) => f.types.includes(value),
    toggle: (f) => ({
      ...f,
      types: f.types.includes(value) ? f.types.filter((t) => t !== value) : [...f.types, value],
    }),
  })),
]

function activeFacetCount(filters) {
  let n = 0
  if (filters.location !== 'all') n += 1
  if (filters.date.kind !== 'all') n += 1
  if (filters.types.length > 0) n += 1
  if (filters.cost !== 'all') n += 1
  return n
}

const SortPill = ({ value, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(value === 'soonest' ? 'latest' : 'soonest')}
    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:border-primary-300"
  >
    <span className="text-gray-400">Sort:</span>
    <strong className="font-semibold text-primary-800">
      Date · {value === 'soonest' ? 'soonest' : 'latest'}
    </strong>
    <ChevronDown size={12} className="text-gray-400" />
  </button>
)

const Events = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sort, setSort] = useState('soonest')
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)

  const filters = useMemo(() => searchParamsToFilters(searchParams), [searchParams])
  const setFilters = (next) => setSearchParams(filtersToSearchParams(next))

  // Debounced search input (200ms), mirrored into the URL
  const [searchInput, setSearchInput] = useState(filters.query)
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filtersRef.current.query) {
        setSearchParams(filtersToSearchParams({ ...filtersRef.current, query: searchInput }), {
          replace: true,
        })
      }
    }, 200)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  // Keep the input in sync when the query changes from outside the box
  // (back/forward navigation, "Clear all filters").
  useEffect(() => {
    setSearchInput(filters.query)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query])

  // Legacy ?eventId= deep link (post-login redirect) → event detail page
  const pendingEventIdRef = useRef(searchParams.get('eventId'))

  useEffect(() => {
    trackEvent('Page Viewed', { page: 'Events' })
  }, [])

  const fetchEvents = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.get('/api/events')
      setEvents(response.data.data)
    } catch (err) {
      console.error('Error fetching events:', err)
      setError('Failed to load events. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  useEffect(() => {
    const eventId = pendingEventIdRef.current
    if (eventId && events.length > 0) {
      pendingEventIdRef.current = null
      const event = events.find((e) => String(e.id) === eventId)
      if (event?.slug) navigate(`/events/${event.slug}`, { replace: true })
    }
  }, [events, navigate])

  const locationCounts = useMemo(() => computeLocationCounts(events), [events])
  const typeCounts = useMemo(() => computeTypeCounts(events), [events])

  const filtered = useMemo(() => {
    const matched = applyEventFilters(events, filters, new Date())
    return [...matched].sort((a, b) =>
      sort === 'soonest'
        ? new Date(a.eventDate) - new Date(b.eventDate)
        : new Date(b.eventDate) - new Date(a.eventDate)
    )
  }, [events, filters, sort])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream-50">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream-50">
        <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-md">
          <div className="mb-4 text-4xl">⚠️</div>
          <p className="mb-4 text-gray-600">{error}</p>
          <Button onClick={fetchEvents}>Try Again</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Slim white search strip */}
      <div className="border-b border-gray-200 bg-primary-50 shadow-sm">
        <div className="mx-auto flex max-w-[1360px] flex-col items-center gap-3 px-4 py-3 lg:px-10">
          <div className="relative w-full max-w-[480px]">
            <Search
              size={16}
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search events, studios, facilitators"
              aria-label="Search events, studios, facilitators"
              className="h-11 w-full rounded-full border border-gray-200 bg-gray-100 pl-11 pr-4 text-sm text-gray-700 placeholder-gray-400 transition hover:bg-white focus:border-primary-500 focus:bg-white focus:outline-none focus:ring-[3px] focus:ring-primary-500/15"
            />
          </div>

          {/* Quick-filter chips (mobile only) */}
          <div className="flex w-full gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:hidden">
            {QUICK_CHIPS.map((chip) => {
              const active = chip.isActive(filters)
              return (
                <button
                  key={chip.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFilters(chip.toggle(filters))}
                  className={`whitespace-nowrap rounded-full border px-3.5 py-[7px] text-xs transition ${
                    active
                      ? 'border-primary-600 bg-primary-600 font-semibold text-cream-50'
                      : 'border-gray-200 bg-white font-medium text-gray-700'
                  }`}
                >
                  {chip.label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Two-pane layout */}
      <div className="mx-auto grid max-w-[1360px] items-start gap-8 px-4 py-10 lg:grid-cols-[280px_minmax(0,1fr)] lg:px-10">
        <EventFilterSidebar
          filters={filters}
          onChange={setFilters}
          locationCounts={locationCounts}
          typeCounts={typeCounts}
        />

        <main>
          <div className="mb-7 flex flex-wrap items-end justify-between gap-6">
            <div>
              <h1 className="font-serif text-[28px] font-medium leading-tight tracking-tight text-primary-800 lg:text-4xl">
                Upcoming experiences <span className="font-normal text-gray-400">· {filtered.length}</span>
            </h1>
              <p className="mt-2 text-sm text-gray-600">{contextLine(filters)}</p>
            </div>
            <div className="hidden lg:block">
              <SortPill value={sort} onChange={setSort} />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white py-20 text-center shadow-sm">
              <div className="mb-4 text-5xl">📅</div>
              <p className="text-xl text-gray-600">No events match these filters.</p>
              <button
                type="button"
                onClick={() => setFilters({ ...DEFAULT_FILTERS, date: { kind: 'all' }, types: [] })}
                className="mt-4 text-sm font-semibold text-primary-600 hover:text-primary-700 hover:underline"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-7 pb-28 md:grid-cols-2 lg:pb-0 xl:grid-cols-3">
              {filtered.map((event) => (
                <ExplorerEventCard
                  key={event.id}
                  event={event}
                  onView={() =>
                    trackEvent('Event Viewed', {
                      event_id: event.id,
                      event_name: event.name,
                      event_format: event.format,
                      event_fee: event.fee || 'N/A',
                      event_date: event.eventDate,
                    })
                  }
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Docked Filters / Sort bar (mobile only) */}
      <div
        className="fixed bottom-9 left-1/2 z-40 flex -translate-x-1/2 items-center rounded-full bg-primary-800 p-1.5 lg:hidden"
        style={{ boxShadow: '0 10px 25px -5px rgba(14,23,15,.5)' }}
      >
        <button
          type="button"
          onClick={() => setFilterSheetOpen(true)}
          className="flex items-center gap-1.5 whitespace-nowrap px-4 py-2 text-[13px] font-semibold text-cream-50"
        >
          <Filter size={14} />
          Filters{activeFacetCount(filters) > 0 && ` · ${activeFacetCount(filters)}`}
        </button>
        <span className="h-[18px] w-px bg-cream-50/25" aria-hidden="true" />
        <button
          type="button"
          onClick={() => setSort(sort === 'soonest' ? 'latest' : 'soonest')}
          className="whitespace-nowrap px-4 py-2 text-[13px] font-semibold text-cream-50"
        >
          Sort: {sort === 'soonest' ? 'Soonest' : 'Latest'}
        </button>
      </div>

      <EventFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        filters={filters}
        onChange={setFilters}
        locationCounts={locationCounts}
        typeCounts={typeCounts}
        resultCount={filtered.length}
      />
    </div>
  )
}

export default Events
